import { CompleteKundali, Language, PlanetName, ZodiacSign } from '../../types/kundali';
import { LifeThemeId } from '../../types/advancedLifeAnalysis';
import { TemporalHorizonAnalysis, TemporalQuarterWindow, TemporalWindowClassification } from '../../types/v518';
import { analyzeFiveRuleHouse } from '../fiveRuleHouseEngine';
import { formatDate } from '../dateUtils';
import { getSavByBhava } from '../vedicEngine';

/* ==========================================================================
   VEDANG JYOTISHAM V5.18 — UNIFIED TEMPORAL SYNTHESIS ENGINE (V518-01)
   Synthesizes Mahadasha, Antardasha, Major Transits (Jupiter, Saturn, Rahu)
   and Sarvashtakvarga (SAV) Bindus into Deterministic Forward Windows
   ========================================================================== */

const THEME_PRIMARY_HOUSES: Record<LifeThemeId, number> = {
  career_job: 10,
  marriage_relationship: 7,
  wealth_finance: 2,
  house_property: 4,
  vehicle: 4,
  children: 5,
  education: 5,
  health_vitality: 1,
  business: 7,
  foreign_travel: 12,
  litigation_competition: 6,
  spirituality: 9,
  family_parents: 4
};

const BENEFIC_PLANETS: PlanetName[] = ['Jupiter', 'Venus', 'Mercury', 'Moon'];
const MALEFIC_PLANETS: PlanetName[] = ['Saturn', 'Mars', 'Rahu', 'Ketu', 'Sun'];

/**
 * Maps sign name to 0-indexed integer (Aries=0 .. Pisces=11)
 */
function getSignIndex(sign: ZodiacSign): number {
  const signs: ZodiacSign[] = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  const idx = (signs || []).indexOf(sign);
  return idx >= 0 ? idx : 0;
}

/**
 * Calculates house from reference sign to target sign (1 to 12)
 */
function getHouseFromSign(refSign: ZodiacSign, targetSign: ZodiacSign): number {
  const refIdx = getSignIndex(refSign);
  const targetIdx = getSignIndex(targetSign);
  return ((targetIdx - refIdx + 12) % 12) + 1;
}

/**
 * Helper to determine approximate transit sign for upcoming quarters
 */
function getEstimatedTransitSigns(quarterOffset: number): {
  jupiterSign: ZodiacSign;
  saturnSign: ZodiacSign;
  rahuSign: ZodiacSign;
} {
  const signs: ZodiacSign[] = [
    'Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo',
    'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces'
  ];
  
  // Baseline ephemeris (current approximate baseline: Jupiter in Taurus/Gemini, Saturn in Aquarius/Pisces, Rahu in Pisces/Aquarius)
  const baseJupIdx = 2; // Gemini
  const baseSatIdx = 11; // Pisces
  const baseRahuIdx = 11; // Pisces

  // Jupiter stays ~12 months per sign (approx 4 quarters)
  const jupShift = Math.floor(quarterOffset / 4);
  // Saturn stays ~30 months per sign (approx 10 quarters)
  const satShift = Math.floor(quarterOffset / 10);
  // Rahu is retrograde, moves ~18 months per sign (approx 6 quarters backward)
  const rahuShift = -Math.floor(quarterOffset / 6);

  const jupiterSign = signs[(baseJupIdx + jupShift) % 12];
  const saturnSign = signs[(baseSatIdx + satShift) % 12];
  const rahuSign = signs[(baseRahuIdx + rahuShift + 12) % 12];

  return { jupiterSign, saturnSign, rahuSign };
}

/**
 * Evaluates whether a transiting planet aspects a specific house
 */
function checkTransitAspect(transitHouseFromLagna: number, targetHouse: number, planet: PlanetName): boolean {
  const diff = ((targetHouse - transitHouseFromLagna + 12) % 12) + 1;
  
  // 7th house aspect applies to all planets
  if (diff === 7 || diff === 1) return true;

  // Special aspects
  if (planet === 'Jupiter' && (diff === 5 || diff === 9)) return true;
  if (planet === 'Saturn' && (diff === 3 || diff === 10)) return true;
  if (planet === 'Mars' && (diff === 4 || diff === 8)) return true;
  if (planet === 'Rahu' && (diff === 5 || diff === 9)) return true;

  return false;
}

export function generateTemporalSynthesis(
  kundali: CompleteKundali,
  themeId: LifeThemeId = 'career_job',
  horizonMonths: 12 | 24 | 36 = 12,
  lang: Language = 'en'
): TemporalHorizonAnalysis {
  const isHi = lang === 'hi';
  const targetHouse = THEME_PRIMARY_HOUSES[themeId] || 10;
  const lagnaHouse = kundali.houses[0];
  const lagnaSign = lagnaHouse?.sign || 'Aries';
  const moon = kundali.planets.find(p => p.name === 'Moon');
  const moonSign = moon?.sign || 'Taurus';

  // 1. Get baseline Natal Promise from V5.17 Five-Rule House Assessment
  const fiveRule = analyzeFiveRuleHouse(kundali, themeId, lang);
  const natalPromiseScore = fiveRule.netPromiseScore; // 0 to 100

  // 2. SAV bindus for the target thematic house
  const savBindus = getSavByBhava(kundali.ashtakvarga, targetHouse, kundali.houses);

  // Calculate SAV Factor: >=30 => +10, 25..29 => 0, <25 => -8
  let savFactor = 0;
  if (savBindus >= 30) savFactor = 10;
  else if (savBindus < 25) savFactor = -8;

  // 3. Find Dasha Progression
  const currentMD = kundali.dashas.find(d => d.isCurrent) || kundali.dashas[0];
  const currentAD = currentMD?.antardashas?.find(ad => ad.isCurrent) || currentMD?.antardashas?.[0];

  const totalQuarters = horizonMonths / 3;
  const quarters: TemporalQuarterWindow[] = [];
  const now = new Date();
  const currentYear = now.getFullYear();
  const currentQuarterIdx = Math.floor(now.getMonth() / 3);

  let cumulativeScore = 0;

  for (let q = 0; q < totalQuarters; q++) {
    const qAbsolute = currentQuarterIdx + q;
    const year = currentYear + Math.floor(qAbsolute / 4);
    const quarterNum = (qAbsolute % 4) + 1;
    const quarterId = `Q${quarterNum}-${year}`;
    
    const startMonth = (quarterNum - 1) * 3;
    const endMonth = startMonth + 2;
    const startDate = formatDate(new Date(year, startMonth, 1));
    const lastDay = new Date(year, endMonth + 1, 0);
    const endDate = formatDate(lastDay);

    // Compute transits for this quarter offset
    const { jupiterSign, saturnSign, rahuSign } = getEstimatedTransitSigns(q);
    const jupHouseFromLagna = getHouseFromSign(lagnaSign, jupiterSign);
    const satHouseFromLagna = getHouseFromSign(lagnaSign, saturnSign);
    const rahuHouseFromLagna = getHouseFromSign(lagnaSign, rahuSign);

    const jupHouseFromMoon = getHouseFromSign(moonSign, jupiterSign);
    const satHouseFromMoon = getHouseFromSign(moonSign, saturnSign);
    const rahuHouseFromMoon = getHouseFromSign(moonSign, rahuSign);

    const jupAspectsLagna = checkTransitAspect(jupHouseFromLagna, 1, 'Jupiter');
    const jupAspectsTheme = checkTransitAspect(jupHouseFromLagna, targetHouse, 'Jupiter');
    const satAspectsLagna = checkTransitAspect(satHouseFromLagna, 1, 'Saturn');
    const satAspectsTheme = checkTransitAspect(satHouseFromLagna, targetHouse, 'Saturn');
    const rahuAspectsTheme = checkTransitAspect(rahuHouseFromLagna, targetHouse, 'Rahu');

    // Dasha contribution
    const mdLord = currentMD?.planet || 'Jupiter';
    const adLord = currentAD?.planet || 'Saturn';
    const isMdBenefic = BENEFIC_PLANETS.includes(mdLord);
    const isAdBenefic = BENEFIC_PLANETS.includes(adLord);

    let dashaScoreDelta = 0;
    if (isMdBenefic && isAdBenefic) dashaScoreDelta += 8;
    else if (isMdBenefic || isAdBenefic) dashaScoreDelta += 3;
    else dashaScoreDelta -= 3;

    // Transit contribution
    let transitScoreDelta = 0;
    const supportingFactors: string[] = [];
    const supportingFactorsHi: string[] = [];
    const limitingFactors: string[] = [];
    const limitingFactorsHi: string[] = [];

    if (jupAspectsTheme || jupHouseFromLagna === targetHouse) {
      transitScoreDelta += 7;
      supportingFactors.push(`Benefic Jupiter activates House ${targetHouse} via transit in ${jupiterSign}`);
      supportingFactorsHi.push(`शुभ गुरु का गोचर ${jupiterSign} राशि से भाव ${targetHouse} को सक्रिय कर रहा है`);
    }

    if (jupAspectsLagna) {
      transitScoreDelta += 4;
      supportingFactors.push(`Jupiter transit supports Lagna vitality & decision clarity`);
      supportingFactorsHi.push(`गुरु गोचर लग्न ऊर्जा और निर्णय शक्ति का समर्थन कर रहा है`);
    }

    if (satAspectsTheme) {
      transitScoreDelta -= 5;
      limitingFactors.push(`Saturn transit exerts structural discipline & delay over House ${targetHouse}`);
      limitingFactorsHi.push(`शनि गोचर भाव ${targetHouse} पर अनुशासन और विलंब का प्रभाव डाल रहा है`);
    }

    if (rahuAspectsTheme) {
      transitScoreDelta -= 3;
      limitingFactors.push(`Rahu transit introduces ambition alongside volatility in House ${targetHouse}`);
      limitingFactorsHi.push(`राहु का प्रभाव भाव ${targetHouse} में महत्वाकांक्षा के साथ अस्थिरता ला सकता है`);
    }

    // Ashtakvarga factor
    if (savFactor > 0) {
      supportingFactors.push(`Strong Sarvashtakvarga (${savBindus} bindus) empowers positive realization`);
      supportingFactorsHi.push(`मजबूत सर्वाष्टकवर्ग (${savBindus} बिंदु) सकारात्मक परिणामों को बल देता है`);
    } else if (savFactor < 0) {
      limitingFactors.push(`Lower Sarvashtakvarga (${savBindus} bindus) requires conservative pacing`);
      limitingFactorsHi.push(`कम सर्वाष्टकवर्ग (${savBindus} बिंदु) सतर्कता और धैर्य की मांग करता है`);
    }

    // Weighted composite calculation: 45% Natal Promise + 35% Dasha + 20% Transit & SAV
    const rawScore = (natalPromiseScore * 0.45) + (50 + dashaScoreDelta * 4) * 0.35 + (50 + (transitScoreDelta + savFactor) * 2.5) * 0.20;
    const netScore = Math.max(15, Math.min(95, Math.round(rawScore)));
    cumulativeScore += netScore;

    let classification: TemporalWindowClassification = 'MODERATE / STEADY';
    if (netScore >= 68) classification = 'SUPPORTIVE';
    else if (netScore < 45) classification = 'CONSTRAINED / CAUTIONARY';

    const labelEn = `${quarterId} (${classification === 'SUPPORTIVE' ? 'Favorable Window' : classification === 'MODERATE / STEADY' ? 'Steady Window' : 'Caution Window'})`;
    const labelHi = `${quarterId} (${classification === 'SUPPORTIVE' ? 'अनुकूल समय' : classification === 'MODERATE / STEADY' ? 'स्थिर समय' : 'सतर्कता समय'})`;

    let actionableGuidance = 'Maintain steady focus; align key initiatives with disciplined execution.';
    let actionableGuidanceHi = 'निरंतर प्रयास जारी रखें और महत्वपूर्ण पहलों को अनुशासन के साथ पूरा करें।';

    if (classification === 'SUPPORTIVE') {
      actionableGuidance = 'Optimal period to initiate expansion, negotiate milestones, and manifest core goals.';
      actionableGuidanceHi = 'विस्तार शुरू करने, महत्वपूर्ण लक्ष्यों को गति देने और नए अवसरों का लाभ उठाने का उत्कृष्ट समय।';
    } else if (classification === 'CONSTRAINED / CAUTIONARY') {
      actionableGuidance = 'Focus on consolidation, debt reduction, skill refinement, and avoiding speculative risks.';
      actionableGuidanceHi = 'स्थिरता बनाए रखने, कौशल संवर्धन करने और जोखिम भरे निर्णयों से बचने पर ध्यान दें।';
    }

    quarters.push({
      quarterId,
      labelEn,
      labelHi,
      startDate,
      endDate,
      classification,
      netScore,
      mahadashaLord: mdLord,
      antardashaLord: adLord,
      majorTransits: [
        { planet: 'Jupiter', transitSign: jupiterSign, houseFromLagna: jupHouseFromLagna, houseFromMoon: jupHouseFromMoon, aspectsLagna: jupAspectsLagna, aspectsDashaLord: false, aspectsThematicHouse: jupAspectsTheme },
        { planet: 'Saturn', transitSign: saturnSign, houseFromLagna: satHouseFromLagna, houseFromMoon: satHouseFromMoon, aspectsLagna: satAspectsLagna, aspectsDashaLord: false, aspectsThematicHouse: satAspectsTheme },
        { planet: 'Rahu', transitSign: rahuSign, houseFromLagna: rahuHouseFromLagna, houseFromMoon: rahuHouseFromMoon, aspectsLagna: false, aspectsDashaLord: false, aspectsThematicHouse: rahuAspectsTheme }
      ],
      savBindus,
      savFactor,
      supportingFactors: supportingFactors.length > 0 ? supportingFactors : ['Stable planetary foundation'],
      supportingFactorsHi: supportingFactorsHi.length > 0 ? supportingFactorsHi : ['स्थिर ग्रह स्थिति'],
      limitingFactors: limitingFactors.length > 0 ? limitingFactors : ['No severe astrological impediments'],
      limitingFactorsHi: limitingFactorsHi.length > 0 ? limitingFactorsHi : ['कोई गंभीर ज्योतिषीय बाधा नहीं'],
      actionableGuidance,
      actionableGuidanceHi,
      confidence: natalPromiseScore > 75 || natalPromiseScore < 35 ? 'HIGH' : 'MEDIUM'
    });
  }

  const averageScore = Math.round(cumulativeScore / quarters.length);
  let overallClassification: TemporalWindowClassification = 'MODERATE / STEADY';
  if (averageScore >= 68) overallClassification = 'SUPPORTIVE';
  else if (averageScore < 45) overallClassification = 'CONSTRAINED / CAUTIONARY';

  // Find best and most challenging quarter
  const sortedQuarters = [...quarters].sort((a, b) => b.netScore - a.netScore);
  const bestQ = sortedQuarters[0];
  const challengingQ = sortedQuarters[sortedQuarters.length - 1];

  return {
    horizonMonths,
    overallClassification,
    averageScore,
    bestWindow: {
      quarterId: bestQ.quarterId,
      labelEn: `${bestQ.quarterId} (Score: ${bestQ.netScore}/100)`,
      labelHi: `${bestQ.quarterId} (स्कोर: ${bestQ.netScore}/100)`,
      reasonEn: bestQ.supportingFactors[0] || 'Maximum alignment of Dasha and planetary transits.',
      reasonHi: bestQ.supportingFactorsHi[0] || 'दशा और ग्रह गोचर का श्रेष्ठतम तालमेल।'
    },
    challengingWindow: {
      quarterId: challengingQ.quarterId,
      labelEn: `${challengingQ.quarterId} (Score: ${challengingQ.netScore}/100)`,
      labelHi: `${challengingQ.quarterId} (स्कोर: ${challengingQ.netScore}/100)`,
      reasonEn: challengingQ.limitingFactors[0] || 'Higher demand for patience and structural discipline.',
      reasonHi: challengingQ.limitingFactorsHi[0] || 'धैर्य और अनुशासित कार्यशैली की आवश्यकता।'
    },
    quarters,
    natalPromiseVsTimingSummary: {
      natalPromiseScore,
      timingActivationScore: averageScore,
      summaryEn: `Natal potential (${natalPromiseScore}%) is currently meeting a ${overallClassification.toLowerCase()} temporal cycle (${averageScore}%). Timing acts as a catalyst, not a replacement for inherent chart capacity.`,
      summaryHi: `जन्मकालीन क्षमता (${natalPromiseScore}%) वर्तमान में ${overallClassification === 'SUPPORTIVE' ? 'अनुकूल' : overallClassification === 'MODERATE / STEADY' ? 'स्थिर' : 'सतर्कतापूर्ण'} समय चक्र (${averageScore}%) से गुजर रही है। समय एक प्रेरक कारक है, जो जन्म क्षमता को सक्रिय करता है।`
    }
  };
}
